import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Music, RefreshCw, Wrench } from 'lucide-react';
import { API, apiFetch } from '../App';

/**
 * Site-wide maintenance page.
 *
 * Two ways it gets shown:
 *   1. Direct URL visit — anyone can go to /maintenance to preview the copy.
 *   2. Automatic redirect — App.js polls /api/site/maintenance on boot; if
 *      `enabled=true` and the user isn't a logged-in admin, all traffic
 *      lands here.
 *
 * Admins are never redirected here — /admin routes stay live so the toggle
 * can be flipped back without any server access.
 */
const MaintenancePage = () => {
  const [message, setMessage] = useState(
    "Wrapped In Music is currently under maintenance. We'll be back shortly.",
  );
  const [updatedAt, setUpdatedAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadStatus = async () => {
    try {
      const res = await fetch(`${API}/site/maintenance`);
      if (res.ok) {
        const data = await res.json();
        if (data.message) setMessage(data.message);
        if (data.updated_at) setUpdatedAt(data.updated_at);
        // If maintenance is OFF, gently bounce the user back to home so they
        // don't stay staring at the maintenance page forever.
        if (data.enabled === false && window.location.pathname === '/maintenance') {
          const params = new URLSearchParams(window.location.search);
          if (params.get('preview') !== '1') {
            window.location.replace('/');
          }
        }
      }
    } catch (_) {
      // Silently keep the default message if the API is unreachable.
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadStatus();
    setTimeout(() => setRefreshing(false), 700);
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-zinc-950 via-purple-950/40 to-zinc-950 text-zinc-100 flex items-center justify-center px-6"
      data-testid="maintenance-page"
    >
      <div className="max-w-2xl w-full">
        {/* Brand */}
        <div className="flex items-center gap-3 mb-10 justify-center">
          <div className="relative">
            <Music className="h-9 w-9 text-purple-300" strokeWidth={1.5} />
            <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-amber-400 animate-pulse" />
          </div>
          <span className="text-lg font-semibold tracking-wide text-zinc-100">
            Wrapped In Music
          </span>
        </div>

        {/* Icon + status */}
        <div className="text-center mb-8">
          <div className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/30 mb-6">
            <Wrench className="h-9 w-9 text-amber-300" strokeWidth={1.5} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold mb-4 tracking-tight">
            We&apos;ll be right back
          </h1>
          <p
            className="text-base text-zinc-300 leading-relaxed max-w-lg mx-auto"
            data-testid="maintenance-message"
          >
            {message}
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-10">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="maintenance-refresh-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-purple-600 hover:bg-purple-500 transition-colors text-sm font-medium disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Check again
          </button>
          <a
            href="mailto:support@wrappedinmusic.com"
            className="text-sm text-zinc-400 hover:text-zinc-200 underline underline-offset-4"
            data-testid="maintenance-support-link"
          >
            Need urgent help? Email support
          </a>
        </div>

        {/* Admin escape hatch — subtle, only visible to those who know to look */}
        <div className="mt-16 text-center text-xs text-zinc-500">
          <Link
            to="/admin"
            className="hover:text-zinc-300 underline underline-offset-4"
            data-testid="maintenance-admin-link"
          >
            Admin
          </Link>
          {updatedAt && (
            <span className="mx-2 text-zinc-700">·</span>
          )}
          {updatedAt && (
            <span>Status set {new Date(updatedAt).toLocaleString()}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaintenancePage;
